import { tapRipple, celebrate, reducedMotion } from './celebrate';

// ─── Ambient interaction layer (installed once, from index.js) ────────────────────
// • every button / menu item tap leaves a small ripple where it was pressed
// • cards marked .fx-card tilt a touch toward the mouse, with a soft gold spotlight
//   following the pointer (mouse only — never on touch screens)
// • click the STR mark in the sidebar for a little burst of gold
// All of it is skipped for "reduce motion".
let installed = false;

export function installFx() {
  if (installed || typeof window === 'undefined' || typeof document === 'undefined') return;
  installed = true;

  document.addEventListener('pointerdown', e => {
    const b = e.target && e.target.closest && e.target.closest('button, .nav-item, .nav-subitem, [role="button"]');
    if (!b || b.disabled || b.hasAttribute('data-no-ripple')) return;
    tapRipple(e.clientX, e.clientY);
  }, { passive: true, capture: true });

  // Tilt + spotlight. Measured against the card's FLAT position, taken once when the
  // pointer arrives and refreshed on scroll/resize — never against its tilted outline.
  // Measuring the tilted card made a feedback loop on big cards (the Buttr task, 2026-10-06):
  // tilting moved the edge out from under the pointer → "left" → flattened → back under
  // the pointer → tilted… many times a second, so the card vibrated. A small margin also
  // stops the card flickering at its edge, and tall cards tilt less (very tall: glow only).
  let active = null;
  let flat = null;                                     // the active card's untilted rect
  const MARGIN = 8;
  const reset = el => { el.style.setProperty('--rx', '0deg'); el.style.setProperty('--ry', '0deg'); el.classList.remove('fx-on'); };
  const leave = () => { if (active) reset(active); active = null; flat = null; };
  const inside = (r, x, y) => x >= r.left - MARGIN && x <= r.right + MARGIN && y >= r.top - MARGIN && y <= r.bottom + MARGIN;
  document.addEventListener('pointermove', e => {
    if (e.pointerType !== 'mouse' || reducedMotion()) return;
    let el = e.target && e.target.closest ? e.target.closest('.fx-card') : null;
    // Still within the card we're on (by its flat outline)? Then stay on it.
    if (active && el !== active && flat && inside(flat, e.clientX, e.clientY)) el = active;
    if (el !== active) {
      if (active) reset(active);
      active = el;
      flat = el ? el.getBoundingClientRect() : null;   // arriving: the card is flat right now
    }
    if (!el || !flat || !flat.width || !flat.height) return;
    const px = Math.min(1, Math.max(0, (e.clientX - flat.left) / flat.width));
    const py = Math.min(1, Math.max(0, (e.clientY - flat.top) / flat.height));
    el.style.setProperty('--mx', (px * 100).toFixed(1) + '%');
    el.style.setProperty('--my', (py * 100).toFixed(1) + '%');
    // Tall cards lean less; very tall ones don't lean at all (the spotlight still follows).
    const k = flat.height > 560 ? 0 : Math.min(1, 240 / flat.height);
    el.style.setProperty('--rx', ((0.5 - py) * 4 * k).toFixed(2) + 'deg');
    el.style.setProperty('--ry', ((px - 0.5) * 5 * k).toFixed(2) + 'deg');
    el.classList.add('fx-on');
  }, { passive: true });
  document.addEventListener('pointerleave', leave, { passive: true });
  window.addEventListener('scroll', leave, { passive: true, capture: true });
  window.addEventListener('resize', leave, { passive: true });

  document.addEventListener('click', e => {
    const m = e.target && e.target.closest && e.target.closest('.sidebar-brand .str-mark');
    if (m) celebrate('approve', m);
  });
}
